import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import { IMaskModule } from 'angular-imask';
import { finalize, forkJoin, Subject, takeUntil, tap } from 'rxjs';
import { IMaskConstants } from '../../../../../core/constants/imask';
import {
    nameOf,
    padFrnWithLeadingZeros,
} from '../../../../../core/functions/string-helpers';
import { DropdownOption } from '../../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../../core/models/form-group-type';
import { GridHelper } from '../../../../../core/models/grid/grid-helper';
import { RegExPattern } from '../../../../../core/models/regexp-pattern/regex-pattern';
import { LoadingService } from '../../../../../core/services/loading/loading.service';
import { DropdownService } from '../../../../../core/services/resources/dropdown.service';
import { FrnEntitySearchRequest } from '../../../../../core/services/uls/models/frn-entity-search-request';
import { FrnEntitySearchResponse } from '../../../../../core/services/uls/models/frn-entity-search-response';
import { ULSService } from '../../../../../core/services/uls/uls.service';
import { RadioButtonCellRendererComponent } from '../../../../../shared/components/grid/cell-renderers/radio-button-cell-renderer/radio-button-cell-renderer.component';
import { DraggableModalWrapperComponent } from '../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { RegexInputDirective } from '../../../../../shared/directives/regex-input/regex-input.directive';
import { FrnSearchTypeEnum } from './constants/frn-search-type';
import { FrnSearchDialogData } from './models/frn-search-dialog-data';
import { FrnSearchFormModel } from './models/frn-search-form-model';
import { SelectTypeaheadDirective } from '../../../../../shared/directives/select-typeahead/select-typeahead.directive';

@Component({
    selector: 'app-frn-search-dialog',
    templateUrl: './frn-search-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        DraggableModalWrapperComponent,
        RegexInputDirective,
        IMaskModule,
        AgGridAngular,
        MatProgressSpinnerModule,
        SelectTypeaheadDirective,
    ],
})
export class FrnSearchDialogComponent implements OnInit, OnDestroy {
    private readonly dialogRef = inject(MatDialogRef<FrnSearchDialogComponent>);
    private readonly dropdownService = inject(DropdownService);
    private readonly ulsService = inject(ULSService);
    private readonly loadingService = inject(LoadingService);
    private readonly data = inject<FrnSearchDialogData>(MAT_DIALOG_DATA);

    destroy$: Subject<void> = new Subject<void>();

    isLoading = false;
    hasSearched = false;
    isResetDisabled = false;
    searchTypeOptions: DropdownOption[] = [];
    stateOptions: DropdownOption[] = [];

    maskOptions = IMaskConstants;
    regExPattern = RegExPattern;

    form: FormGroup<FormGroupType<FrnSearchFormModel>> = new FormGroup<
        FormGroupType<FrnSearchFormModel>
    >({
        searchType: new FormControl<FrnSearchTypeEnum | null>(null, [
            Validators.required,
        ]),
        search: new FormControl<string | null>(null, [Validators.required]),
        licenseeState: new FormControl<string | null>(null),
        licenseeCity: new FormControl<string | null>(null),
        licenseeZipCode: new FormControl<string | null>(null),
    });

    selectedFrnContact = new FormControl<string | null>(null);
    context = {
        formControl: this.selectedFrnContact,
    };
    gridApi: GridApi | undefined;
    rowData: FrnEntitySearchResponse[] = [];
    columnDefs: ColDef[] = [
        {
            headerName: 'Select',
            field: nameOf<FrnEntitySearchResponse>('licenseeId'),
            filter: false,
            sortable: false,
            width: 80,
            minWidth: 80,
            pinned: 'left',
            cellRenderer: RadioButtonCellRendererComponent,
            ...GridHelper.disableCellFocusBorderColDef,
        },
        {
            headerName: 'Licensee',
            field: nameOf<FrnEntitySearchResponse>('entityName'),
            minWidth: 200,
        },
        {
            headerName: 'FRN',
            field: nameOf<FrnEntitySearchResponse>('frn'),
            filter: false,
            minWidth: 150,
            valueFormatter: (params) => {
                return padFrnWithLeadingZeros(params.value?.toString()) ?? '';
            },
        },
        {
            headerName: 'Street Address',
            field: nameOf<FrnEntitySearchResponse>('streetAddress'),
            sortable: false,
            minWidth: 180,
        },
        {
            headerName: 'PO Box',
            field: nameOf<FrnEntitySearchResponse>('poBox'),
            sortable: false,
            minWidth: 70,
        },
        {
            headerName: 'City, State Zip',
            minWidth: 180,
            valueGetter: (params) => {
                return this.formatCityStateZip(params.data);
            },
            valueFormatter: (params) => {
                return this.formatCityStateZip(params.data);
            },
        },
    ];

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
    };

    ngOnInit(): void {
        this.loadingService.suppressGlobalSpinner(this.destroy$);
        this.loadDropdownData();
        this.setupSearchTypeChangeHandler();
        this.applyInitialFrnSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onCancel(): void {
        this.isLoading = false;
        this.dialogRef.close();
    }

    onSearch(): void {
        if (this.form.invalid) {
            return;
        }

        const formValue = this.form.value;
        const request: FrnEntitySearchRequest = {
            stateCode: formValue.licenseeState || undefined,
            cityName: formValue.licenseeCity || undefined,
            postalCode: formValue.licenseeZipCode || undefined,
        };

        const searchType = formValue.searchType;
        const searchValue = formValue.search;

        // Set the request based on the search type and value
        if (searchValue) {
            if (searchType === FrnSearchTypeEnum.Frn) {
                const frnNumber = parseInt(searchValue);
                if (!isNaN(frnNumber)) {
                    request.frn = frnNumber;
                }
            } else if (searchType === FrnSearchTypeEnum.Name) {
                request.companyOrIndividualName = searchValue;
            }
        }

        this.isLoading = true;
        this.hasSearched = true;
        this.isResetDisabled = false;
        this.ulsService
            .searchEntities(request)
            .pipe(
                takeUntil(this.destroy$),
                tap((response) => {
                    this.rowData = response ?? [];
                    this.gridApi?.setGridOption('rowData', this.rowData);
                }),
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe({
                error: (error) => {
                    console.error('Error searching for FRN entities:', error);
                    this.rowData = [];
                    this.gridApi?.setGridOption('rowData', []);
                },
            });
    }

    onGridReady(params: GridReadyEvent<FrnEntitySearchResponse>): void {
        this.gridApi = params.api;
    }

    onClearSelection(): void {
        this.selectedFrnContact.setValue(null);
    }

    onSaveAndReturn(): void {
        const licenseeId = this.selectedFrnContact.value;
        if (!licenseeId) {
            return;
        }

        const selectedEntity = this.rowData.find(
            (row) => row.licenseeId === licenseeId,
        );

        if (selectedEntity) {
            this.dialogRef.close(selectedEntity);
        }
    }

    onResetSearch(): void {
        // Clear form fields
        this.form.patchValue({
            search: null,
            licenseeState: null,
            licenseeCity: null,
            licenseeZipCode: null,
        });

        // Clear grid results
        this.rowData = [];
        this.gridApi?.setGridOption('rowData', []);

        // Clear selection
        this.onClearSelection();

        // Disable reset button
        this.isResetDisabled = true;
    }

    isSearchTypeFrn(): boolean {
        return this.form.controls.searchType.value === FrnSearchTypeEnum.Frn;
    }

    isSearchTypeName(): boolean {
        return this.form.controls.searchType.value === FrnSearchTypeEnum.Name;
    }

    private formatCityStateZip(data: FrnEntitySearchResponse): string {
        const parts = [data.city, data.state, data.zip].filter(Boolean);
        return parts.join(', ');
    }

    private applyInitialFrnSearch(): void {
        const initialFrn = this.data.frn?.trim();
        if (!initialFrn) {
            return;
        }

        this.form.controls.searchType.setValue(FrnSearchTypeEnum.Frn);
        this.form.controls.search.setValue(initialFrn);
    }

    private setupSearchTypeChangeHandler(): void {
        this.form.controls.searchType.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((searchType: FrnSearchTypeEnum | null) => {
                    const searchControl = this.form.controls.search;

                    searchControl.setValue(null);
                    searchControl.setValidators(Validators.required);

                    if (searchType === FrnSearchTypeEnum.Name) {
                        searchControl.addValidators(Validators.minLength(3));
                    } else if (searchType === FrnSearchTypeEnum.Frn) {
                        searchControl.addValidators(Validators.maxLength(10));
                    }

                    searchControl.updateValueAndValidity();
                }),
            )
            .subscribe();
    }

    private loadDropdownData(): void {
        forkJoin({
            searchTypeOptions: this.dropdownService.getFrnSearchTypeOptions(),
            stateOptions: this.dropdownService.getStateCodes(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap(({ searchTypeOptions, stateOptions }) => {
                    this.searchTypeOptions = searchTypeOptions;
                    this.stateOptions = stateOptions;
                }),
            )
            .subscribe();
    }
}
